// src/pages/ProfilePage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  doc,
  getDocs,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getRoleLabel } from '../constants/userRoles';
import { useUser } from '../components/UserContext';
import UserAvatar, {
  resolveUserPhotoURL,
} from '../components/UserAvatar';
import {
  removeUserAvatar,
  uploadUserAvatar,
  validateAvatarFile,
} from '../services/avatarService';
import { invalidateAuthorProfileCache } from '../hooks/useNewsAuthor';
import {
  getCachedUserProfile,
  loadUserProfile,
  setCachedUserProfile,
} from '../services/userProfileService';
import { canEditProfile } from './profilePermissions';
import {
  buildProfileUpdate,
  canShowLastVisit,
  getEmploymentSummary,
  hasEditorialHistory,
  hasSportsPreferences,
  normalizeSportsPreferences,
  PROFILE_LIMITS,
  toDate,
  toDateInputValue,
  validateEmploymentForm,
  validateProfileForm,
} from './profileUtils';
import {
  loadAuthorArticlesCount,
  loadCommentsCount,
} from '../services/profileStatsService';
import { updateUserEmployment } from '../services/adminUsersService';
import {
  getSectionRoute,
  newsSectionRoutes,
} from '../config/newsSectionRoutes';
import AdminArticlesList from './AdminArticlesList';
import ProfileSkeleton from './ProfileSkeleton';
import PageSeo from '../components/seo/PageSeo';
import logo from '../img/logo.png';
import {
  claimAnalyticsAction,
  identifyUser,
  trackEvent,
} from '../amplitude';
import '../styles/ProfilePage.css';

const createProfileForm = (profile) => ({
  username: profile?.username || '',
  city: profile?.city || '',
  country: profile?.country || '',
  birthDate: toDateInputValue(profile?.birthDate),
  bio: profile?.bio || '',
  sportsPreferences: {
    favoriteSport: profile?.sportsPreferences?.favoriteSport || '',
    favoriteTeam: profile?.sportsPreferences?.favoriteTeam || '',
    favoriteNationalTeam:
      profile?.sportsPreferences?.favoriteNationalTeam || '',
    favoriteAthlete: profile?.sportsPreferences?.favoriteAthlete || '',
    favoriteTournament:
      profile?.sportsPreferences?.favoriteTournament || '',
    favoriteTeamRoute:
      profile?.sportsPreferences?.favoriteTeamRoute || '',
  },
  showLastVisit: profile?.showLastVisit !== false,
});

const getAvatarErrorMessage = (error) => {
  const messages = {
    'storage/unauthorized': 'Немає дозволу на завантаження фото',
    'storage/unauthenticated': 'Сесія завершилась. Увійдіть повторно',
    'storage/quota-exceeded': 'Перевищено ліміт сховища',
    'storage/invalid-format': 'Підтримуються лише JPG, PNG або WebP',
  };

  if (
    error?.message?.startsWith('Підтримуються') ||
    error?.message?.startsWith('Максимальний')
  ) {
    return error.message;
  }

  return messages[error?.code] || 'Не вдалося завантажити фото. Спробуйте ще раз';
};

const ProfilePage = () => {
  const { userId } = useParams();
  const {
    user: currentUser,
    userData: currentUserData,
    loadingUser,
    updateCurrentUserProfile,
  } = useUser();

  const [userData, setUserData] = useState(
    () => getCachedUserProfile(userId) || null
  );
  const [profileInitialLoading, setProfileInitialLoading] = useState(
    () => !getCachedUserProfile(userId)
  );
  const [profileRefreshing, setProfileRefreshing] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState('');
  const [articlesCount, setArticlesCount] = useState(null);
  const trackedProfileRef = useRef(null);

  // edit state
  const [activeProfileTab, setActiveProfileTab] = useState('profile');
  const [form, setForm] = useState(() => createProfileForm(null));
  const [savedForm, setSavedForm] = useState(() => createProfileForm(null));
  const [commentsCount, setCommentsCount] = useState(null);
  const [commentsError, setCommentsError] = useState(false);
  const [editorialArticleFallback, setEditorialArticleFallback] = useState(false);
  const [employmentEditing, setEmploymentEditing] = useState(false);
  const [employmentSaving, setEmploymentSaving] = useState(false);
  const [employmentForm, setEmploymentForm] = useState({
    startedAt: '',
    endedAt: '',
    lastRole: 'author',
  });

  // ui state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);
  const profileMountedAt = useRef(performance.now());

  // ✅ чи може редагувати (тільки власник)
  const effectiveCurrentUid = currentUser?.uid || auth.currentUser?.uid;
  const canEdit = canEditProfile(effectiveCurrentUid, userId);
  const ownProfileData = canEdit ? currentUserData : null;
  const ownProfileAuthLoading = canEdit ? loadingUser : false;
  const isAdminViewer = currentUserData?.role === 'admin';

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PROFILE MOUNT] ${profileMountedAt.current.toFixed(1)}ms`);
    }
  }, []);

  useEffect(() => () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  useEffect(() => {
    let active = true;
    const requestStartedAt = performance.now();
    const cachedProfile = ownProfileData
      ? { id: userId, ...ownProfileData }
      : getCachedUserProfile(userId);

    const applyProfile = (profile) => {
      if (!active) return;
      const nextForm = createProfileForm(profile);
      setUserData(profile);
      setForm(nextForm);
      setSavedForm(nextForm);
    };

    setActiveProfileTab('profile');
    setError('');
    setSuccess('');
    setProfileLoadError('');
    setArticlesCount(null);
    setAvatarFile(null);
    setAvatarPreview('');

    if (!userId) {
      setUserData(null);
      setProfileInitialLoading(false);
      setProfileLoadError('Профіль користувача не знайдено');
      return () => {
        active = false;
      };
    }

    if (cachedProfile) {
      applyProfile(cachedProfile);
      if (ownProfileData) {
        setCachedUserProfile(userId, cachedProfile);
      }
      setProfileInitialLoading(false);
      setProfileRefreshing(true);

      if (process.env.NODE_ENV === 'development') {
        console.debug(`[PROFILE CACHE HIT] profile:${userId}`);
        console.debug(
          `[PROFILE FIRST RENDER] ${(performance.now() - requestStartedAt).toFixed(1)}ms`
        );
        console.debug('[PROFILE SKELETON HIDDEN] cached');
      }
    } else {
      setUserData(null);
      setProfileInitialLoading(true);
      setProfileRefreshing(false);
    }

    if (canEdit && ownProfileAuthLoading && !ownProfileData) {
      return () => {
        active = false;
      };
    }

    if (ownProfileData) {
      setProfileInitialLoading(false);
      setProfileRefreshing(false);
      return () => {
        active = false;
      };
    }

    if (process.env.NODE_ENV === 'development') {
      console.debug(`[PROFILE USER REQUEST START] profile:${userId}`);
    }

    loadUserProfile(userId)
      .then((profile) => {
        if (!active) return;

        applyProfile(profile);
        setProfileInitialLoading(false);
        setProfileRefreshing(false);

        if (!profile) {
          setProfileLoadError('Профіль користувача не знайдено');
        }

        if (process.env.NODE_ENV === 'development') {
          const elapsed = (performance.now() - requestStartedAt).toFixed(1);
          console.debug(`[PROFILE USER REQUEST COMPLETE] ${elapsed}ms`);
          console.debug(`[PROFILE FIRST RENDER] ${elapsed}ms`);
          console.debug('[PROFILE SKELETON HIDDEN] loaded');
        }
      })
      .catch((requestError) => {
        if (!active) return;

        setProfileInitialLoading(false);
        setProfileRefreshing(false);
        setProfileLoadError(
          requestError?.code === 'permission-denied'
            ? 'У вас немає доступу до цього профілю'
            : 'Не вдалося завантажити профіль користувача'
        );

        if (process.env.NODE_ENV === 'development') {
          console.error('Помилка завантаження профілю:', requestError);
        }
      });

    return () => {
      active = false;
    };
  }, [canEdit, ownProfileAuthLoading, ownProfileData, userId]);

  useEffect(() => {
    if (!userData || !userId) return undefined;
    let active = true;
    setCommentsCount(null);
    setCommentsError(false);
    setEditorialArticleFallback(false);

    loadCommentsCount(userId)
      .then((count) => {
        if (active) setCommentsCount(count);
      })
      .catch(() => {
        if (active) setCommentsError(true);
      });

    if (!hasEditorialHistory(userData)) {
      loadAuthorArticlesCount(userId)
        .then((count) => {
          if (!active) return;
          setArticlesCount(count);
          setEditorialArticleFallback(count > 0);
        })
        .catch(() => {
          if (active) setArticlesCount(0);
        });
    }

    return () => {
      active = false;
    };
  }, [userData, userId]);

  const handleAvatarFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const validationError = validateAvatarFile(file);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setSuccess('');
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const cancelAvatarPreview = () => {
    setAvatarFile(null);
    setAvatarPreview('');
    fileInputRef.current?.focus();
  };

  const confirmAvatarUpload = async () => {
    if (!canEdit || !avatarFile || avatarUploading) return;

    setAvatarUploading(true);
    setError('');
    setSuccess('');

    try {
      const result = await uploadUserAvatar(avatarFile, userData.avatarPath);
      const updatedProfile = { ...userData, ...result };
      setUserData(updatedProfile);
      updateCurrentUserProfile(result);
      invalidateAuthorProfileCache(currentUser.uid);
      setCachedUserProfile(currentUser.uid, updatedProfile);
      setAvatarFile(null);
      setAvatarPreview('');
      setSuccess('Фото профілю оновлено');
    } catch (uploadError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Помилка завантаження аватара:', uploadError);
      }
      setError(getAvatarErrorMessage(uploadError));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!canEdit || avatarUploading) return;
    if (!window.confirm('Видалити фото профілю?')) return;

    setAvatarUploading(true);
    setError('');
    setSuccess('');

    try {
      await removeUserAvatar(userData.avatarPath);
      const updates = { photoURL: null, avatarPath: null };
      const updatedProfile = { ...userData, ...updates };
      setUserData(updatedProfile);
      updateCurrentUserProfile(updates);
      invalidateAuthorProfileCache(currentUser.uid);
      setCachedUserProfile(currentUser.uid, updatedProfile);
      setSuccess('Фото профілю видалено');
    } catch (deleteError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Помилка видалення аватара:', deleteError);
      }
      setError(getAvatarErrorMessage(deleteError));
    } finally {
      setAvatarUploading(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = toDate(timestamp);
    if (date) {
      return date.toLocaleDateString('uk-UA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return 'Дата невідома';
  };

  const onChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const startEdit = () => {
    setError('');
    setSuccess('');
    setActiveProfileTab('edit');
  };

  const cancelEdit = () => {
    setError('');
    setSuccess('');
    setForm(savedForm);
    setActiveProfileTab('profile');
  };

  // ✅ якщо адмін змінив username — оновлюємо всі news.author зі старого на новий
  const migrateAdminAuthorName = async (oldUsername, newUsername) => {
    if (!oldUsername || !newUsername || oldUsername === newUsername) return;

    const qNews = query(collection(db, 'news'), where('author', '==', oldUsername));
    const snap = await getDocs(qNews);
    if (snap.empty) return;

    const docs = snap.docs;
    let i = 0;

    while (i < docs.length) {
      const batch = writeBatch(db);
      const chunk = docs.slice(i, i + 500);

      chunk.forEach((d) => {
        batch.update(d.ref, { author: newUsername });
      });

      await batch.commit();
      i += 500;
    }
  };

  const saveProfile = async () => {
    setError('');
    setSuccess('');

    // ✅ додатковий захист
    if (!canEdit) {
      setError('Ви не можете редагувати чужий профіль.');
      return;
    }

    const v = validateProfileForm(form);
    if (v) {
      setError(v);
      return;
    }

    setSaving(true);
    try {
      const oldUsername = userData?.username || '';
      const newUsername = form.username.trim();

      const editableProfileFields = buildProfileUpdate(form);
      editableProfileFields.sportsPreferences.favoriteTeamRoute = getSectionRoute(
        editableProfileFields.sportsPreferences.favoriteTeam,
        'Футбол України'
      ) || '';
      const payload = {
        ...editableProfileFields,
        updatedAt: serverTimestamp(),
      };
      const authenticatedUid = auth.currentUser?.uid;

      if (!authenticatedUid || authenticatedUid !== userId) {
        setError(
          !authenticatedUid
            ? 'Сесія завершилась. Увійдіть повторно'
            : 'Ви не можете редагувати чужий профіль.'
        );
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.debug('[PROFILE UPDATE PAYLOAD]', {
          uid: authenticatedUid,
          keys: Object.keys(payload),
          payload,
        });
      }

      // 1) update user
      const userRef = doc(db, 'users', authenticatedUid);
      await updateDoc(userRef, payload);

      // 2) if admin and username changed => update all news.author
      if (userData?.role === 'admin' && oldUsername && newUsername && oldUsername !== newUsername) {
        await migrateAdminAuthorName(oldUsername, newUsername);
      }

      // 3) update local state
      const updatedProfile = { ...userData, ...editableProfileFields };
      setUserData(updatedProfile);
      updateCurrentUserProfile(editableProfileFields);
      identifyUser(authenticatedUid, {
        ...updatedProfile,
        authProvider: auth.currentUser?.providerData?.[0]?.providerId,
      });
      invalidateAuthorProfileCache(authenticatedUid);
      setCachedUserProfile(authenticatedUid, updatedProfile);
      const nextSavedForm = createProfileForm(updatedProfile);
      setForm(nextSavedForm);
      setSavedForm(nextSavedForm);
      setActiveProfileTab('profile');
      setSuccess('Профіль успішно оновлено');
      const changedFields = Object.keys(editableProfileFields).filter(
        (key) => JSON.stringify(userData?.[key]) !== JSON.stringify(editableProfileFields[key])
      );
      trackEvent('profile_updated', {
        changed_fields: changedFields,
        added_sports_preferences: hasSportsPreferences(
          editableProfileFields.sportsPreferences
        ),
        changed_avatar: false,
      });

    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Помилка збереження профілю:', e);
      }
      if (e?.code === 'permission-denied') {
        setError('Не вдалося зберегти профіль через обмеження доступу');
      } else if (e?.code === 'unauthenticated') {
        setError('Сесія завершилась. Увійдіть повторно');
      } else {
        setError('Не вдалося зберегти зміни. Спробуйте ще раз.');
      }
    } finally {
      setSaving(false);
    }
  };

  const employment = getEmploymentSummary(userData);
  const showEditorialContent =
    hasEditorialHistory(userData) || editorialArticleFallback;
  const displayedEmployment = employment || (editorialArticleFallback ? {
    active: false,
    status: 'Раніше працював у BigSport',
    role: 'author',
    roleLabel: 'Автор',
    startedAt: null,
    endedAt: null,
  } : null);
  const preferencesVisible = hasSportsPreferences(userData?.sportsPreferences);
  const displayedSportsPreferences = normalizeSportsPreferences(
    userData?.sportsPreferences
  );
  const lastVisitVisible = canShowLastVisit({
    isOwner: canEdit,
    isAdmin: isAdminViewer,
    showLastVisit: userData?.showLastVisit,
  });
  const formDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  useEffect(() => {
    if (!userData || trackedProfileRef.current === userId) return;
    trackedProfileRef.current = userId;
    if (!claimAnalyticsAction(`profile_viewed:${userId}`)) return;
    trackEvent('profile_viewed', {
      profile_type: canEdit
        ? 'own'
        : showEditorialContent
          ? 'author'
          : 'user',
      viewer_role: currentUserData?.role || 'guest',
      has_editorial_history: showEditorialContent,
    });
  }, [
    canEdit,
    currentUserData?.role,
    showEditorialContent,
    userData,
    userId,
  ]);

  const profileTabs = [
    { id: 'profile', label: 'Профіль' },
    ...(canEdit ? [{ id: 'edit', label: 'Редагувати профіль' }] : []),
    { id: 'statistics', label: 'Статистика' },
  ];

  const changeProfileTab = (nextTab) => {
    if (nextTab === activeProfileTab) return;
    if (
      activeProfileTab === 'edit' &&
      formDirty &&
      !window.confirm('У вас є незбережені зміни. Вийти без збереження?')
    ) {
      return;
    }

    if (activeProfileTab === 'edit') {
      setForm(savedForm);
    }
    if (nextTab === 'edit') {
      setError('');
      setSuccess('');
    }
    setActiveProfileTab(nextTab);
  };

  const handleTabKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = profileTabs.findIndex(
      (tab) => tab.id === activeProfileTab
    );
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex =
      (currentIndex + direction + profileTabs.length) % profileTabs.length;
    changeProfileTab(profileTabs[nextIndex].id);
  };

  const formatShortDate = (value) => {
    const date = toDate(value);
    return date
      ? date.toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      : 'Дату не вказано';
  };

  const startEmploymentEdit = () => {
    setEmploymentForm({
      startedAt: toDateInputValue(displayedEmployment?.startedAt),
      endedAt: toDateInputValue(displayedEmployment?.endedAt),
      lastRole: displayedEmployment?.role || 'author',
    });
    setEmploymentEditing(true);
  };

  const saveEmployment = async () => {
    const validationError = validateEmploymentForm({
      ...employmentForm,
      isActive: displayedEmployment?.active,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setEmploymentSaving(true);
    setError('');
    try {
      const result = await updateUserEmployment(userId, employmentForm);
      const updatedProfile = {
        ...userData,
        employment: result.employment,
        employmentHistory: result.employmentHistory,
      };
      setUserData(updatedProfile);
      setCachedUserProfile(userId, updatedProfile);
      setEmploymentEditing(false);
      setSuccess('Період роботи оновлено');
    } catch (employmentError) {
      setError(
        employmentError?.message ||
        'Не вдалося оновити період роботи'
      );
    } finally {
      setEmploymentSaving(false);
    }
  };

  if (profileInitialLoading && !userData) {
    return (
      <>
        <PageSeo
          title="Профіль користувача | BigSPORT"
          description="Профіль користувача спортивного порталу BigSPORT."
          canonicalPath={`/profile/${userId}`}
          robots="noindex, follow"
        />
        <ProfileSkeleton showEditAction={canEdit} />
      </>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <PageSeo
          title="Профіль користувача | BigSPORT"
          description="Профіль користувача спортивного порталу BigSPORT."
          canonicalPath={`/profile/${userId}`}
          robots="noindex, follow"
        />
        <div className="profile-card profile-error-state">
          <p>{profileLoadError || 'Профіль користувача не знайдено'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container" aria-busy={profileRefreshing}>
      <PageSeo
        title="Профіль користувача | BigSPORT"
        description="Профіль користувача спортивного порталу BigSPORT."
        canonicalPath={`/profile/${userId}`}
        robots="noindex, follow"
      />
      <div className="profile-card">
        <h1>Профіль користувача</h1>

        <div className="profile-avatar-section">
          <UserAvatar
            photoURL={userData.photoURL}
            fallbackPhotoURL={
              canEdit ? resolveUserPhotoURL(null, currentUser) : null
            }
            name={userData.username || userData.displayName}
            email={userData.email}
            size={144}
            loading="eager"
            onLoad={() => {
              if (process.env.NODE_ENV === 'development') {
                console.debug(
                  `[PROFILE AVATAR LOADED] ${
                    (performance.now() - profileMountedAt.current).toFixed(1)
                  }ms`
                );
              }
            }}
          />
          <div className="profile-avatar-identity">
            <strong>{userData.username || userData.displayName || 'Користувач'}</strong>
            {(canEdit || isAdminViewer) && <span>{userData.email || '—'}</span>}
            {(userData.city || userData.country) && (
              <span>{[userData.city, userData.country].filter(Boolean).join(', ')}</span>
            )}
            {userData.bio && <p className="profile-bio">{userData.bio}</p>}
          </div>

          {canEdit && activeProfileTab === 'edit' && (
            <div className="profile-avatar-actions">
              <label
                className={`profile-btn profile-avatar-file-label ${
                  avatarUploading ? 'disabled' : ''
                }`}
                htmlFor="profile-avatar-file"
              >
                {resolveUserPhotoURL(userData, currentUser)
                  ? 'Змінити фото'
                  : 'Додати фото'}
              </label>
              <input
                ref={fileInputRef}
                id="profile-avatar-file"
                className="profile-avatar-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarFile}
                disabled={avatarUploading}
              />
              {userData.photoURL && (
                <button
                  type="button"
                  className="profile-btn profile-avatar-delete"
                  onClick={handleAvatarDelete}
                  disabled={avatarUploading}
                >
                  Видалити фото
                </button>
              )}
            </div>
          )}
        </div>

        {(error || success) && (
          <div className={`profile-alert ${error ? 'error' : 'success'}`}>
            {error || success}
          </div>
        )}

        <div
          className="profile-tabs"
          role="tablist"
          aria-label="Розділи профілю"
          onKeyDown={handleTabKeyDown}
        >
          {profileTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              id={`profile-tab-${tab.id}`}
              className={`profile-tab ${
                activeProfileTab === tab.id ? 'active' : ''
              }`}
              role="tab"
              aria-selected={activeProfileTab === tab.id}
              aria-controls={`profile-panel-${tab.id}`}
              tabIndex={activeProfileTab === tab.id ? 0 : -1}
              onClick={() => changeProfileTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeProfileTab === 'profile' && (
          <div
            id="profile-panel-profile"
            className="profile-tab-panel"
            role="tabpanel"
            aria-labelledby="profile-tab-profile"
          >
            <table className="profile-table">
              <tbody>
                <tr>
                  <th>Ім'я користувача</th>
                  <td>{userData.username || userData.displayName || 'Користувач'}</td>
                </tr>
                {(canEdit || isAdminViewer) && (
                  <tr>
                    <th>Email</th>
                    <td>{userData.email}</td>
                  </tr>
                )}
                <tr>
                  <th>Дата реєстрації</th>
                  <td>{formatDate(userData.createdAt)}</td>
                </tr>
                {userData.city && (
                  <tr><th>Місто</th><td>{userData.city}</td></tr>
                )}
                {userData.country && (
                  <tr><th>Країна</th><td>{userData.country}</td></tr>
                )}
                <tr>
                  <th>Роль</th>
                  <td className={`role-${userData.role || 'user'}`}>
                    {getRoleLabel(userData.role)}
                  </td>
                </tr>
                {userData.birthDate && (
                  <tr>
                    <th>Дата народження</th>
                    <td>{formatShortDate(userData.birthDate)}</td>
                  </tr>
                )}
                {[
                  ['favoriteSport', 'Улюблений вид спорту'],
                  ['favoriteTeam', 'Улюблена команда'],
                  ['favoriteNationalTeam', 'Улюблена збірна'],
                  ['favoriteAthlete', 'Улюблений спортсмен'],
                  ['favoriteTournament', 'Улюблений турнір'],
                ].map(([key, label]) => displayedSportsPreferences[key] && (
                  <tr key={key}>
                    <th>{label}</th>
                    <td>
                      {key === 'favoriteTeam' &&
                      displayedSportsPreferences.favoriteTeamRoute ? (
                        <Link
                          className="profile-team-link"
                          to={displayedSportsPreferences.favoriteTeamRoute}
                        >
                          {displayedSportsPreferences[key]}
                        </Link>
                      ) : displayedSportsPreferences[key]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!preferencesVisible && canEdit && (
              <button
                type="button"
                className="profile-preferences-prompt"
                onClick={startEdit}
              >
                Додати спортивні вподобання
              </button>
            )}

            {displayedEmployment && (
              <section className="profile-section profile-employment">
                <div className="profile-employment-header">
                  <div className="profile-employment-brand">
                    <img src={logo} alt="BigSport" />
                    <div>
                      <h2>Робота в BigSport</h2>
                      <span className="profile-employment-badge">
                        {displayedEmployment.active
                          ? 'Чинний працівник'
                          : 'Колишній працівник'}
                      </span>
                    </div>
                  </div>
                  {isAdminViewer && !employmentEditing && (
                    <button
                      type="button"
                      className="profile-btn profile-employment-edit"
                      onClick={startEmploymentEdit}
                    >
                      Скоригувати період
                    </button>
                  )}
                </div>
                {employmentEditing ? (
                  <div className="profile-employment-form">
                    <label>
                      <span>Дата початку роботи</span>
                      <input
                        type="date"
                        className="profile-input"
                        value={employmentForm.startedAt}
                        onChange={(event) => setEmploymentForm((current) => ({
                          ...current,
                          startedAt: event.target.value,
                        }))}
                      />
                    </label>
                    <label>
                      <span>Дата завершення</span>
                      <input
                        type="date"
                        className="profile-input"
                        value={employmentForm.endedAt}
                        disabled={displayedEmployment.active}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(event) => setEmploymentForm((current) => ({
                          ...current,
                          endedAt: event.target.value,
                        }))}
                      />
                    </label>
                    <label>
                      <span>Остання посада</span>
                      <select
                        className="profile-input"
                        value={employmentForm.lastRole}
                        onChange={(event) => setEmploymentForm((current) => ({
                          ...current,
                          lastRole: event.target.value,
                        }))}
                      >
                        <option value="author">Автор</option>
                        <option value="admin">Адміністратор</option>
                      </select>
                    </label>
                    <div className="profile-employment-actions">
                      <button
                        type="button"
                        className="profile-btn primary"
                        onClick={saveEmployment}
                        disabled={employmentSaving}
                      >
                        {employmentSaving ? 'Збереження…' : 'Зберегти період'}
                      </button>
                      <button
                        type="button"
                        className="profile-btn"
                        onClick={() => setEmploymentEditing(false)}
                        disabled={employmentSaving}
                      >
                        Скасувати
                      </button>
                    </div>
                  </div>
                ) : (
                  <dl className="profile-employment-details">
                    <div><dt>Статус</dt><dd>{displayedEmployment.status}</dd></div>
                    <div>
                      <dt>{displayedEmployment.active ? 'Посада' : 'Остання посада'}</dt>
                      <dd>{displayedEmployment.roleLabel}</dd>
                    </div>
                    <div>
                      <dt>Період роботи</dt>
                      <dd>
                        {formatShortDate(displayedEmployment.startedAt)} —{' '}
                        {displayedEmployment.active
                          ? 'теперішній час'
                          : formatShortDate(displayedEmployment.endedAt)}
                      </dd>
                    </div>
                    <div>
                      <dt>Написано новин</dt>
                      <dd>
                        {articlesCount === null
                          ? <span className="profile-stat-skeleton" />
                          : articlesCount}
                      </dd>
                    </div>
                  </dl>
                )}
              </section>
            )}

            {showEditorialContent && (userData?.id || userId) && (
              <AdminArticlesList
                uid={userData.id || userId}
                username={userData.username}
                onCountChange={setArticlesCount}
              />
            )}
          </div>
        )}

        {activeProfileTab === 'edit' && canEdit && (
          <div
            id="profile-panel-edit"
            className="profile-tab-panel"
            role="tabpanel"
            aria-labelledby="profile-tab-edit"
          >
            <div className="profile-actions">
              <button
                className="profile-btn primary"
                onClick={saveProfile}
                disabled={saving}
              >
                {saving ? 'Збереження...' : 'Зберегти'}
              </button>
              <button className="profile-btn" onClick={cancelEdit} disabled={saving}>
                Скасувати
              </button>
            </div>
            <h2 className="profile-edit-heading">Основна інформація</h2>
            <table className="profile-table">
              <tbody>
                <tr>
                  <th>Ім'я користувача</th>
                  <td>
                  <input
                    className="profile-input"
                    value={form.username}
                    onChange={onChange('username')}
                    placeholder="Введіть ім'я користувача"
                    disabled={saving}
                  />
                  </td>
                </tr>
                <tr><th>Email</th><td>{userData.email}</td></tr>
                <tr>
                  <th>Місто</th>
                  <td>
                  <input
                    className="profile-input"
                    value={form.city}
                    onChange={onChange('city')}
                    placeholder="Наприклад: Київ"
                    disabled={saving}
                  />
                  </td>
                </tr>
                <tr>
                  <th>Країна</th>
                  <td>
                  <input
                    className="profile-input"
                    value={form.country}
                    onChange={onChange('country')}
                    placeholder="Наприклад: Україна"
                    disabled={saving}
                  />
                  </td>
                </tr>
                <tr>
                  <th>Дата народження</th>
                  <td>
                    <input
                      className="profile-input"
                      type="date"
                      value={form.birthDate}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={onChange('birthDate')}
                      disabled={saving}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Про себе</th>
                  <td>
                    <textarea
                      className="profile-input profile-textarea"
                      value={form.bio}
                      onChange={onChange('bio')}
                      maxLength={PROFILE_LIMITS.bio}
                      placeholder="Коротко розкажіть про себе"
                      disabled={saving}
                    />
                    <small className="profile-character-count">
                      {form.bio.length} / {PROFILE_LIMITS.bio}
                    </small>
                  </td>
                </tr>
                <tr>
                  <th>Роль</th>
                  <td className={`role-${userData.role || 'user'}`}>
                    {getRoleLabel(userData.role)}
                  </td>
                </tr>
              </tbody>
            </table>
            <section className="profile-section profile-edit-section">
            <h2>Спортивні вподобання</h2>
            <div className="profile-form-grid">
              {[
                ['favoriteSport', 'Улюблений вид спорту'],
                ['favoriteTeam', 'Улюблена команда'],
                ['favoriteNationalTeam', 'Улюблена збірна'],
                ['favoriteAthlete', 'Улюблений спортсмен'],
                ['favoriteTournament', 'Улюблений турнір або чемпіонат'],
              ].map(([key, label]) => (
                <label key={key}>
                  <span>{label}</span>
                  <input
                    className="profile-input"
                    value={form.sportsPreferences[key]}
                    list={key === 'favoriteTeam' ? 'profile-team-options' : undefined}
                    maxLength={PROFILE_LIMITS.preference}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      sportsPreferences: {
                        ...current.sportsPreferences,
                        [key]: event.target.value,
                      },
                    }))}
                    disabled={saving}
                  />
                  {key === 'favoriteTeam' && (
                    <datalist id="profile-team-options">
                      {newsSectionRoutes
                        .filter((item) =>
                          item.category === 'Футбол України' &&
                          !['УПЛ', 'Кубок України', 'Збірна України',
                            'Перша Ліга', 'Друга Ліга'].includes(item.section)
                        )
                        .map((item) => (
                          <option value={item.section} key={item.path} />
                        ))}
                      <option value="Інша команда" />
                    </datalist>
                  )}
                </label>
              ))}
            </div>
            <h2>Приватність</h2>
            <label className="profile-privacy-control">
              <input
                type="checkbox"
                checked={form.showLastVisit}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  showLastVisit: event.target.checked,
                }))}
                disabled={saving}
              />
              Показувати іншим користувачам останній візит
            </label>
            </section>
          </div>
        )}

        {activeProfileTab === 'statistics' && (
          <div
            id="profile-panel-statistics"
            className="profile-tab-panel"
            role="tabpanel"
            aria-labelledby="profile-tab-statistics"
          >
            <section className="profile-section profile-statistics-section">
              <h2>Статистика</h2>
              <div className="profile-stat-grid">
                <div className="profile-stat-card">
                  <span>Коментарі</span>
                  <strong>
                    {commentsCount === null && !commentsError
                      ? <span className="profile-stat-skeleton" />
                      : commentsError ? 'Недоступно' : commentsCount}
                  </strong>
                </div>
                <div className="profile-stat-card">
                  <span>Дата реєстрації</span>
                  <strong>{formatShortDate(userData.createdAt)}</strong>
                </div>
                {lastVisitVisible && userData.lastVisitAt && (
                  <div className="profile-stat-card">
                    <span>Останній візит</span>
                    <strong>{formatDate(userData.lastVisitAt)}</strong>
                  </div>
                )}
                {showEditorialContent && (
                  <div className="profile-stat-card">
                    <span>Написано новин</span>
                    <strong>
                      {articlesCount === null
                        ? <span className="profile-stat-skeleton" />
                        : articlesCount}
                    </strong>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

      </div>

      {avatarFile && (
        <div className="profile-avatar-modal-backdrop">
          <div
            className="profile-avatar-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="avatar-preview-title"
          >
            <h2 id="avatar-preview-title">Попередній перегляд фото</h2>
            <UserAvatar
              photoURL={avatarPreview}
              name={userData.username}
              email={userData.email}
              size={180}
              loading="eager"
            />
            <div className="profile-avatar-modal-actions">
              <button
                type="button"
                className="profile-btn"
                onClick={cancelAvatarPreview}
                disabled={avatarUploading}
              >
                Скасувати
              </button>
              <button
                type="button"
                className="profile-btn primary"
                onClick={confirmAvatarUpload}
                disabled={avatarUploading}
              >
                {avatarUploading ? 'Завантаження…' : 'Підтвердити фото'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
